/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { requestOpt } from 'api'
import clsx from 'clsx'
import { Button, Icon, Input, Typography } from 'components'
import { useToast } from 'hooks'
import {
	FormOtpProps,
	Icons,
	OTPLoginForm,
	OTPResponse,
	TypographyTypes
} from 'interfaces'
import React, { useEffect, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'

export const FormOtp: React.FC<FormOtpProps> = ({
	bodyLogin,
	singin,
	setLoading
}) => {
	const { showToast } = useToast()
	const {
		control,
		handleSubmit,
		formState: { isValid }
	} = useForm<OTPLoginForm>({
		defaultValues: {
			digit1: '',
			digit2: '',
			digit3: '',
			digit4: ''
		}
	})
	const [timer, setTimer] = useState(900)

	const rules = {
		required: true,
		maxLength: 1,
		pattern: /^[0-9]$/
	}

	useEffect(() => {
		const interval = setInterval(() => {
			setTimer(prevTimer => (prevTimer > 0 ? prevTimer - 1 : 0))
		}, 1000)
		return () => clearInterval(interval)
	}, [])

	useEffect(() => {
		const firstInput = document.getElementById('digit1')
		if (firstInput) {
			;(firstInput as HTMLInputElement).focus()
		}
	}, [])

	const formatTime = (seconds: number): string => {
		const minutes = Math.floor(seconds / 60)
		const remainingSeconds = seconds % 60
		return `${minutes.toString().padStart(2, '0')}:${remainingSeconds
			.toString()
			.padStart(2, '0')}`
	}

	const digitInputs = ['digit1', 'digit2', 'digit3', 'digit4']

	const onSubmit: SubmitHandler<OTPLoginForm> = async data => {
		const code = parseInt(
			`${data.digit1}${data.digit2}${data.digit3}${data.digit4}`,
			10
		)
		if (bodyLogin) {
			const body = {
				...bodyLogin,
				otp_code: code
			}
			singin(body)
		}
	}

	const handleInputFocus = (
		event: React.ChangeEvent<HTMLInputElement>,
		index: number
	) => {
		const { value } = event.target
		if (value.match(/^[0-9]$/)) {
			// Si el valor es válido y hay un input siguiente, enfoca el siguiente
			const nextInput = document.getElementById(digitInputs[index + 1])
			if (nextInput) {
				;(nextInput as HTMLInputElement).focus()
			}
		}
	}

	const handleBackspace = (
		event: React.KeyboardEvent<HTMLInputElement>,
		index: number,
		fieldValue: string
	) => {
		// Si presiona "Backspace" y el campo está vacío, enfoca el campo anterior
		if (event.key === 'Backspace' && !fieldValue && index > 0) {
			const prevInput = document.getElementById(digitInputs[index - 1])
			if (prevInput) {
				;(prevInput as HTMLInputElement).focus()
			}
		}
	}

	const handleRequestOTP = () => {
		if (bodyLogin) {
			setLoading(true)
			const body = {
				community_id: bodyLogin.community_id,
				signin_type: bodyLogin.signin_type,
				signin_username: bodyLogin.signin_username,
				signin_value: bodyLogin.signin_value,
				device_type: bodyLogin.device_type
			}
			requestOpt(body).then((response: OTPResponse | null): void => {
				setLoading(false)
				if (response?.detail) {
					showToast(response.detail, '', 'error')
				}
			})
		}
	}

	return (
		<form
			className="w-[386px]  h-[400px] bg-transparent-default p-6 rounded-[16px] flex flex-col justify-center items-center border-[1px] border-white"
			onSubmit={handleSubmit(onSubmit as SubmitHandler<OTPLoginForm>)}
			data-testid="otp-form"
		>
			<Typography
				title="Security validation"
				fontLeading="22/27"
				type={TypographyTypes.h1}
				className="text-white mb-6 !font-normal"
			/>
			<Typography
				title="Please enter the 4 digit code sent to your email."
				fontLeading="12/12"
				type={TypographyTypes.p}
				className="text-white mb-6 !font-normal !text-base text-center"
			/>
			<div className="flex justify-between gap-8">
				{digitInputs.map((name, index) => (
					<Controller
						key={name}
						name={name as 'digit1' | 'digit2' | 'digit3' | 'digit4'}
						control={control}
						rules={rules}
						render={({ field }) => (
							<Input
								{...field}
								className="flex items-center justify-center w-14 h-14 text-3xl text-center !bg-white text-black rounded-lg !border-[1px] border-blue-primary border-solid"
								maxLength={1}
								inputClassName="!border-none !p-1 !text-black"
								inputFieldClassName="text-center"
								inputMode="numeric"
								pattern="[0-9]"
								data-testid={`otp-input-${index + 1}`}
								onChange={e => {
									const { value } = e.target
									if (/^[0-9]$/.test(value) || value === '') {
										field.onChange(value)
										handleInputFocus(e, index)
									}
								}}
								onKeyDown={e => handleBackspace(e, index, field.value)}
							/>
						)}
					/>
				))}
			</div>
			<div className="flex items-center justify-center gap-2 py-4">
				<Icon src={Icons.timer} fillPath className="text-black-1 w-6 h-6" />
				<Typography
					title={formatTime(timer)}
					fontLeading="12/12"
					type={TypographyTypes.p}
					className="text-black-1 !font-normal !text-base"
				/>
			</div>
			<Button
				label="Continue"
				type="submit"
				color="Transparent"
				className={clsx(
					'!min-w-[unset] min-h-[54px] !w-[168px] !py-3 !px-6 !bg-white border-[1px] border-blue-primary border-solid mb-2',
					{
						'shadow-[0_4px_10px_rgba(255,255,255,0.5)]': timer === 0 && isValid,
						'hover:shadow-[0_6px_15px_rgba(255,255,255,0.7)]':
							timer === 0 && isValid,
						'transition-shadow duration-300 ease-in-out': true
					}
				)}
				disabled={!isValid}
				data-testid="otp-submit"
			/>
			{timer === 0 && (
				<Button
					label="Request a new code"
					className="!min-w-[unset] !w-max !border-none !py-4 !px-6 underline text-white"
					color="Transparent"
					id="resend-code"
					data-testid="resend-otp-btn"
					onClick={handleRequestOTP}
				/>
			)}
		</form>
	)
}
